import React, {createContext, useCallback, useEffect, useRef, useState} from 'react';
import {useBreakpointBetween} from 'bonprix-design-system';
import Landmark from '@core/scripts/components/landmark-adapter.js';
import History from '@core/scripts/components/history.js';

export const HeaderContext = createContext();

const TRANSPARENT_CLASS = '-transparent';
const LIGHT_CLASS = '-light';
const HEADER_HOVER_DELAY = 400;

export const HeaderProvider = ({isTransparent, lightMode, headerWrapper, children}) => {
    const [headerLightMode, setHeaderLightMode] = useState(lightMode);
    const [mobileNaviOpened, setMobileNaviOpened] = useState(false);

    const mobileHeader = useBreakpointBetween('s', 'xl');

    const hoverFocusTimer = useRef(null);

    useEffect(() => {
        if (!mobileHeader) {
            closeMobileNavi(false); // close mobile navi on breakpoint change to desktop
        }

        const handleScroll = () => {
            if (mobileHeader) {
                setColorScheme();
            }
        };

        const handleMouseEnterAndFocusIn = () => {
            if (hoverFocusTimer.current) {
                clearTimeout(hoverFocusTimer.current);
            }
            applyDefaultColorScheme();
        };

        const handleMouseLeaveAndFocusOut = () => {
            hoverFocusTimer.current = setTimeout(() => {
                if (!headerWrapper.querySelector(':focus')) {
                    setColorScheme();
                }
            }, HEADER_HOVER_DELAY);
        };

        if (isTransparent) {
            headerWrapper.addEventListener('focusin', handleMouseEnterAndFocusIn);
            headerWrapper.addEventListener('focusout', handleMouseLeaveAndFocusOut);

            if (mobileHeader) {
                window.addEventListener('scroll', handleScroll);
            } else {
                headerWrapper.addEventListener('mouseenter', handleMouseEnterAndFocusIn);
                headerWrapper.addEventListener('mouseleave', handleMouseLeaveAndFocusOut);
            }

            return () => {
                headerWrapper.removeEventListener('focusin', handleMouseEnterAndFocusIn);
                headerWrapper.removeEventListener('focusout', handleMouseLeaveAndFocusOut);

                if (mobileHeader) {
                    window.removeEventListener('scroll', handleScroll);
                } else {
                    headerWrapper.removeEventListener('mouseenter', handleMouseEnterAndFocusIn);
                    headerWrapper.removeEventListener('mouseleave', handleMouseLeaveAndFocusOut);
                }
            };
        }
    }, [mobileHeader, isTransparent, headerWrapper, lightMode]);

    const setColorScheme = () => {
        if (window.scrollY === 0) {
            applyGivenColorScheme();
        } else {
            applyDefaultColorScheme();
        }
    };

    const applyGivenColorScheme = useCallback(() => {
        setHeaderLightMode(lightMode);
        if (isTransparent) {
            headerWrapper?.classList.add(TRANSPARENT_CLASS);
            if (lightMode) {
                headerWrapper?.classList.add(LIGHT_CLASS);
            }
        }
    }, [lightMode, isTransparent, headerWrapper]);

    const applyDefaultColorScheme = useCallback(() => {
        setHeaderLightMode(false);
        if (isTransparent) {
            headerWrapper?.classList.remove(TRANSPARENT_CLASS);
            headerWrapper?.classList.remove(LIGHT_CLASS);
        }
    }, [isTransparent, headerWrapper]);

    const openMobileNavi = () => {
        setMobileNaviOpened(true);
        History.push('menu');
        Landmark.sendUserInterfaceLandmark({navi: 'menu\\\\\\'});
        document.body.classList.add('in-navigation');
    };

    const closeMobileNavi = (sendLandmark = true) => {
        setMobileNaviOpened(false);
        History.remove('menuChannel');
        History.remove('menu');
        document.body.classList.remove('in-navigation');

        if (document.activeElement) {
            // Ensures the header color scheme updates correctly by removing focus from the hamburger button
            document.activeElement.blur()
        }

        if (sendLandmark) {
            Landmark.sendUserInterfaceLandmark({navi: 'menu\\\\close\\'});
        }
    };

    return (
        <HeaderContext.Provider value={{headerLightMode, mobileNaviOpened, openMobileNavi, closeMobileNavi}}>
            {children}
        </HeaderContext.Provider>
    );
};
